/**
 * Decision 0139 (memory phase 1, 1.7): how big a run's context pack was, by file and, for AGENTS.md and the turn file
 * (`.memory/turn.md`), by `##` section. Data, not a cap or a metric: it lets a person see what a 0.5–2 M-token lead
 * turn was made of. Cloud and web only (never sent to a runner). `tokens` is an estimate (about 4 bytes a token).
 *
 * Decision 0145 adds the rest of a turn's context: the cloud mind's replayed chat history by kind (`history`), and the
 * provider's reported usage of the run (`usage`), so pack, history and what the model was billed for sit side by side.
 */
import { Schema } from "effect"
import { NonNegInt } from "./ids"
import { CallContext } from "./run"

export const ContextSection = Schema.Struct({ heading: Schema.String, bytes: NonNegInt })
export type ContextSection = typeof ContextSection.Type

export const ContextFileSize = Schema.Struct({
  path: Schema.String,
  bytes: NonNegInt,
  /** `##` sections in file order (the text before the first one is "(top)"); empty for other files. */
  sections: Schema.Array(ContextSection),
})
export type ContextFileSize = typeof ContextFileSize.Type

/**
 * Decision 0145: what a part of the replayed chat history is.
 * - `person`: people's chat messages (text); `assistant`: the agent's own replies (text);
 * - `tool`: tool calls and their results (inputs, outputs, errors);
 * - `wake`: earlier turns' prompts, resent byte-identically (0088 §2), and wake lines from before 0088;
 * - `prompt`: this turn's prompt (the pack's `.memory/turn.md`, so the pack counts it too);
 * - `digest`: the archive digest (0137); `compaction`: a provider compaction summary (0088 §3);
 * - `file`: file and image parts; `other`: any other part the model sees.
 */
export const CONTEXT_PART_KINDS = [
  "person",
  "assistant",
  "tool",
  "wake",
  "prompt",
  "digest",
  "compaction",
  "file",
  "other",
] as const
export const ContextPartKind = Schema.Literals(CONTEXT_PART_KINDS)
export type ContextPartKind = typeof ContextPartKind.Type

/** One kind's share of the history: UTF-8 bytes and how many parts. */
export const HistorySection = Schema.Struct({ kind: ContextPartKind, bytes: NonNegInt, parts: NonNegInt })
export type HistorySection = typeof HistorySection.Type

/**
 * Decision 0145: the history a cloud turn sent with its first model request, measured by the body before the call.
 * `bytes` / `tokens` are what the model reads: everything from the newest compaction block on (Anthropic drops what
 * precedes it). `droppedBytes` is what was sent before that block (request bytes, not billed tokens). The system prompt
 * (AGENTS.md, plus a recovery line) and the tool definitions come on top of the history in every request.
 */
export const HistorySize = Schema.Struct({
  /** Messages the model reads (after the newest compaction). */
  messages: NonNegInt,
  bytes: NonNegInt,
  tokens: NonNegInt,
  sections: Schema.Array(HistorySection),
  droppedMessages: NonNegInt,
  droppedBytes: NonNegInt,
  systemBytes: NonNegInt,
  /** The tool definitions (names, descriptions, input schemas) as JSON. */
  toolBytes: NonNegInt,
  tools: NonNegInt,
})
export type HistorySize = typeof HistorySize.Type

/**
 * Decision 0145: the run's usage rows as the provider reported them, summed (core `contextUsage`; uncached input as
 * core `usageRowV2` reads it). `contextTokens` = uncached input + cache reads + cache writes over all model calls;
 * `perStepTokens` its average per call (every call reads the whole context again).
 */
export const ContextUsage = Schema.Struct({
  steps: NonNegInt,
  contextTokens: NonNegInt,
  uncachedInputTokens: NonNegInt,
  cachedInputTokens: NonNegInt,
  cacheWriteTokens: NonNegInt,
  outputTokens: NonNegInt,
  perStepTokens: NonNegInt,
  /** The context of the main-loop model calls (`Usage.calls` of the primary row; runners from 0.9.23, cloud minds). */
  calls: Schema.optionalKey(CallContext),
})
export type ContextUsage = typeof ContextUsage.Type

export const ContextSize = Schema.Struct({
  /** The pack's hash (`Run.context.hash`). */
  hash: Schema.String,
  bytes: NonNegInt,
  tokens: NonNegInt,
  files: Schema.Array(ContextFileSize),
  /** Decision 0145: where the run thought (`cloud`: the AI SDK in the body; `runner`: a CLI session). */
  mind: Schema.optionalKey(Schema.Literals(["cloud", "runner"])),
  /** Decision 0145: a runner run that resumed its CLI session (the session's context comes on top of the pack). */
  resumed: Schema.optionalKey(Schema.Boolean),
  /** Decision 0145: a cloud turn's replayed history (absent: a runner run, or a turn from before 0145). */
  history: Schema.optionalKey(HistorySize),
  /** Decision 0145: the provider's reported usage (absent: the run has not ended or reported none). */
  usage: Schema.optionalKey(ContextUsage),
})
export type ContextSize = typeof ContextSize.Type
